import { useCallback, useEffect, useRef, useState } from 'react';
import { interactiveServiceFetch } from '../services/interactiveServiceFetch';

/**
 * Custom hook for Server-Sent Events (SSE) streaming using fetch + ReadableStream.
 * Provides reliable, resumable streaming with automatic reconnection.
 */
export const useSSEStream = (baseUrl, runId, onMessage, completionEventTypes = []) => {
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    
    const abortControllerRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const backoffRef = useRef(1000); // Start with 1s backoff
    const isStoppedRef = useRef(false);
    const readerRef = useRef(null);
    const onMessageRef = useRef(onMessage); // Store callback in ref to avoid reconnections
    const completionEventTypesRef = useRef(completionEventTypes); // Same: stable across renders

    const lastEventIdRef = useRef('0-0'); // Store lastEventId in ref to avoid triggering reconnections

    const MAX_BACKOFF = 30000; // Max 30s backoff

    // Update the refs when values change, but don't trigger reconnections
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        completionEventTypesRef.current = completionEventTypes;
    }, [completionEventTypes]);

    const cleanup = useCallback(() => {
        // Cancel ongoing fetch
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Cancel reader
        if (readerRef.current) {
            try {
                readerRef.current.cancel();
            } catch (e) {
                // Ignore errors during cancel
            }
            readerRef.current = null;
        }

        // Clear reconnect timeout
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        setIsConnected(false);
    }, []);

    const connect = useCallback(async () => {
        if (isStoppedRef.current || !runId) {
            return;
        }

        cleanup();

        try {
            // Create new abort controller for this connection
            abortControllerRef.current = new AbortController();

            // Build URL
            const url = `${baseUrl.replace(/\/$/, '')}/streams/${encodeURIComponent(runId)}`;

            console.log('[useSSEStream] Connecting to SSE:', { url, lastEventId: lastEventIdRef.current });

            // Fetch with cookie-based auth and Last-Event-ID header (only if resuming)
            const headers = {
                'Accept': 'text/event-stream',
            };

            // Only send Last-Event-ID if we're resuming from a previous connection
            if (lastEventIdRef.current && lastEventIdRef.current !== '0-0') {
                headers['Last-Event-ID'] = lastEventIdRef.current;
            }

            const abortControlSignal = abortControllerRef.current.signal;

            const response = await interactiveServiceFetch(url, {
                headers,
                signal: abortControlSignal,
            });

            if (!response.ok) {
                if (response.status >= 400 && response.status < 500) {
                    console.warn('[useSSEStream] Stream unavailable, not retrying:', response.status);
                    isStoppedRef.current = true;
                    setError(`SSE connection failed: ${response.status} ${response.statusText}`);
                    cleanup();
                    return;
                }
                throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            setIsConnected(true);
            setError(null);
            backoffRef.current = 1000; // Reset backoff on successful connection

            // Read stream
            const reader = response.body.getReader();
            readerRef.current = reader;
            const decoder = new TextDecoder();
            let buffer = '';

            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        console.log('[useSSEStream] Stream ended');
                        break;
                    }

                    // Decode chunk and add to buffer
                    buffer += decoder.decode(value, { stream: true });

                    // Process complete lines
                    let newlineIndex;
                    while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
                        const line = buffer.slice(0, newlineIndex).trim();
                        buffer = buffer.slice(newlineIndex + 1);

                        // Skip empty lines and comments (keepalive)
                        if (!line || line.startsWith(':')) {
                            continue;
                        }

                        // Parse SSE field
                        const colonIndex = line.indexOf(':');
                        if (colonIndex === -1) {
                            continue;
                        }

                        const field = line.slice(0, colonIndex);
                        const value = line.slice(colonIndex + 1).trim();

                        if (field === 'id') {
                            lastEventIdRef.current = value;
                        } else if (field === 'event') {
                            // Event type - we'll handle this in the next data field
                        } else if (field === 'data') {
                            try {
                                // Parse JSON message
                                const message = JSON.parse(value);

                                // Call message handler using ref to avoid reconnections
                                if (onMessageRef.current) {
                                    onMessageRef.current(message);
                                }

                                // Check for stream completion events
                                const eventType = message.type;
                                if (completionEventTypesRef.current.includes(eventType)) {
                                    // Stop reconnection attempts
                                    isStoppedRef.current = true;
                                    cleanup();
                                    return;
                                }
                            } catch (err) {
                                console.error('[useSSEStream] Failed to parse message:', err, value);
                            }
                        }
                    }
                }
            } catch (readErr) {
                // When the user aborts (stop button), `reader.read()` rejects
                // with an AbortError / DOMException. Browsers differ:
                //   - Chrome: "AbortError: BodyStreamBuffer was aborted"
                //   - Safari/Firefox: DOMException with name === 'AbortError'
                // In some browsers this rejection bypasses the outer catch,
                // surfacing as an "Uncaught (in promise) AbortError". Swallow
                // it here when cancellation is what we asked for.
                if (
                    abortControlSignal.aborted &&
                    (readErr?.name === 'AbortError' || readErr instanceof DOMException)
                ) {
                    return;
                }
                throw readErr;
            }

            // Stream ended naturally - attempt reconnect unless stopped
            if (!isStoppedRef.current) {
                console.log('[useSSEStream] Stream ended, reconnecting...');
                scheduleReconnect();
            }

        } catch (err) {
            if (err.name === 'AbortError') return;

            setError(err.message);
            setIsConnected(false);

            // Reconnect with backoff unless stopped
            if (!isStoppedRef.current) scheduleReconnect();
        }
    }, [baseUrl, runId, cleanup]);

    const scheduleReconnect = useCallback(() => {
        if (isStoppedRef.current) {
            return;
        }

        const delay = Math.min(backoffRef.current, MAX_BACKOFF);
        console.log(`[useSSEStream] Reconnecting in ${delay}ms`);

        reconnectTimeoutRef.current = setTimeout(() => {
            backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF);
            connect();
        }, delay);
    }, [connect]);

    const disconnect = useCallback(() => {
        console.log('[useSSEStream] Disconnecting');
        isStoppedRef.current = true;
        cleanup();
    }, [cleanup]);

    // Auto-connect when runId is available
    useEffect(() => {
        if (runId) {
            isStoppedRef.current = false;
            lastEventIdRef.current = '0-0'; // Reset event ID for new stream
            connect();
        }

        return () => {
            disconnect();
        };
    }, [runId, connect, disconnect]);

    return {
        isConnected,
        error,
        disconnect,
    };
};

export default useSSEStream;

